import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export function qcPortalSessionKey(platform, brandId) {
  return `qc_portal_session_${platform}_${brandId}`;
}

export default function QcPortalLoginForm({ compact = false }) {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [qcPortalEmail, setQcPortalEmail] = useState('');
  const [qcPassword, setQcPassword] = useState('');
  const [qcChallengeId, setQcChallengeId] = useState(null);
  const [qcOtp, setQcOtp] = useState('');

  const inputClass = compact
    ? `w-full px-4 py-3 rounded-xl border ${
        isDarkMode ? 'bg-slate-800/50 border-slate-700/50 text-white' : 'bg-white border-slate-300 text-slate-900'
      }`
    : `w-full px-4 py-3.5 rounded-xl border ${
        isDarkMode ? 'bg-slate-800/50 border-slate-700/50 text-white' : 'bg-white border-slate-300 text-slate-900'
      }`;

  const handleQcStart = async () => {
    if (!qcPortalEmail.trim() || !qcPassword) {
      toast.error('Enter portal email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/qc-login/start', {
        portalEmail: qcPortalEmail.trim(),
        password: qcPassword,
      });
      if (res.data?.success && res.data.challengeId) {
        setQcChallengeId(res.data.challengeId);
        setQcPassword('');
        toast.success(res.data.message || 'Enter the OTP from Zepto');
      } else {
        toast.error(res.data?.message || 'Login failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Quick Commerce login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQcVerifyOtp = async () => {
    if (!qcOtp.trim() || qcOtp.trim().length < 4) {
      toast.error('Enter the 4-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/qc-login/verify-otp', {
        challengeId: qcChallengeId,
        otp: qcOtp.trim(),
      });
      if (res.data?.success && res.data.token) {
        const sessions = res.data.portalSessions || [];
        for (const s of sessions) {
          if (s.platform && s.brandId && s.sessionToken) {
            sessionStorage.setItem(qcPortalSessionKey(s.platform, s.brandId), s.sessionToken);
          }
        }
        await loginWithToken(res.data.token, res.data.user);
        toast.success('Welcome!');
        navigate(res.data.redirectTo || '/dashboard', { replace: true });
      } else {
        toast.error(res.data?.message || 'OTP verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {!qcChallengeId ? (
        <>
          <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
            Use your Zepto portal email and password, then enter the 4-digit OTP from Zepto.
          </p>
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-700'}`}
            >
              Portal email
            </label>
            <input
              type="email"
              className={inputClass}
              value={qcPortalEmail}
              onChange={(e) => setQcPortalEmail(e.target.value)}
              placeholder="portal@yourstore.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-700'}`}
            >
              Portal password
            </label>
            <input
              type="password"
              className={inputClass}
              value={qcPassword}
              onChange={(e) => setQcPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <button
            type="button"
            disabled={loading || !qcPortalEmail.trim() || !qcPassword}
            onClick={handleQcStart}
            className="w-full rounded-xl bg-orange-600 py-3.5 text-sm font-semibold text-white disabled:opacity-50 hover:bg-orange-700 transition-colors"
          >
            {loading ? 'Checking…' : 'Continue to OTP'}
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-medium text-orange-600">Step 2 of 2</span>
            <span>Enter the Zepto OTP</span>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
            Use the code from the latest Zepto &quot;Email Otp&quot; message for this store.
          </p>
          <input
            className={`${inputClass} text-center tracking-[0.35em] text-lg font-semibold`}
            value={qcOtp}
            onChange={(e) => setQcOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="••••"
            maxLength={4}
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          <button
            type="button"
            disabled={loading || qcOtp.length < 4}
            onClick={handleQcVerifyOtp}
            className="w-full rounded-xl bg-orange-600 py-3.5 text-sm font-semibold text-white disabled:opacity-50 hover:bg-orange-700 transition-colors"
          >
            {loading ? 'Verifying…' : 'Sign in to your store'}
          </button>
          <button
            type="button"
            className="text-xs text-slate-500 w-full hover:text-slate-700"
            onClick={() => {
              setQcChallengeId(null);
              setQcOtp('');
            }}
          >
            ← Back
          </button>
        </>
      )}
    </div>
  );
}
